import { Zap, User, Eye, Trophy, BookOpen } from "lucide-react";

const ideas = [
    {
        title: "Micro-Interactions",
        description: "Enhance user engagement with subtle animations and feedback mechanisms that make interactions feel alive and intuitive.",
        icon: Zap,
    },
    {
        title: "Personalization",
        description: "Implement personalized user experiences based on behavior, preferences, and location, creating a more relevant and engaging interface.",
        icon: User,
    },
    {
        title: "Accessibility Focus",
        description: "Design with inclusivity in mind, ensuring that your product is usable for everyone, regardless of their abilities or disabilities.",
        icon: Eye,
    },
    {
        title: "Gamification Elements",
        description: "Incorporate game-like features to motivate users and increase engagement, such as rewards, challenges, and progress tracking.",
        icon: Trophy,
    },
    {
        title: "Storytelling through Design",
        description: "Use narrative techniques to guide users through your interface, making their journey enjoyable and memorable.",
        icon: BookOpen,
    },
];

const CardContent = ({ item }: { item: typeof ideas[0] }) => {
    const Icon = item.icon;
    return (
        <>
            {/* Mobile + Tablet layout — always visible, icon + title + desc */}
            <div className="lg:hidden flex flex-col p-5 gap-3">
                <div className="flex items-center gap-3">
                    <div
                        className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600"
                        style={{ boxShadow: "0 4px 20px rgba(147,51,234,0.4)" }}
                    >
                        <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="text-white text-sm font-bold leading-snug">{item.title}</h4>
                </div>
                <p className="text-xs sm:text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
                    {item.description}
                </p>
            </div>

            {/* Desktop layout — hover animation */}
            <div className="hidden lg:flex relative z-10 h-full flex-col items-center justify-center p-6 text-center gap-3">
                <div
                    className="icon-wrap w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600"
                    style={{ boxShadow: "0 4px 20px rgba(147,51,234,0.4)" }}
                >
                    <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="title-wrap">
                    <h4 className="text-white text-lg font-bold">{item.title}</h4>
                </div>
                <div className="desc-wrap absolute bottom-0 left-0 right-0 px-6 pb-6 text-center">
                    <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
                        {item.description}
                    </p>
                </div>
            </div>
        </>
    );
};

export default function UIUXIdeas() {
    return (
        <section className="relative py-12 lg:py-24 overflow-hidden">
            <style>{`
                .idea-card .icon-wrap {
                    transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease;
                    transform: translateY(0);
                    opacity: 1;
                }
                .idea-card:hover .icon-wrap {
                    transform: translateY(-10px);
                    opacity: 0;
                }

                .idea-card .title-wrap {
                    transition: transform 0.4s cubic-bezier(0.4,0,0.2,1);
                    transform: translateY(0);
                }
                .idea-card:hover .title-wrap {
                    transform: translateY(-56px);
                }

                .idea-card .desc-wrap {
                    transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease;
                    transform: translateY(20px);
                    opacity: 0;
                    padding-top: 4px;
                }
                .idea-card:hover .desc-wrap {
                    transform: translateY(0);
                    opacity: 1;
                }

                .idea-card {
                    transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
                }
                .idea-card:hover {
                    transform: translateY(-4px);
                    border-color: rgba(147,51,234,0.4) !important;
                    box-shadow: 0 0 32px rgba(147,51,234,0.15);
                }
            `}</style>

            {/* Ambient glows */}
            <div className="pointer-events-none absolute inset-0">
                <div
                    className="absolute top-[10%] left-[5%] w-[300px] h-[240px] sm:w-[500px] sm:h-[400px] rounded-full blur-[100px]"
                    style={{ background: "rgba(147,51,234,0.07)" }}
                />
                <div
                    className="absolute bottom-[10%] right-[5%] w-[260px] h-[200px] sm:w-[420px] sm:h-[340px] rounded-full blur-[100px]"
                    style={{ background: "rgba(59,130,246,0.07)" }}
                />
            </div>

            <div className="container relative z-10">

                {/* Header */}
                <div className="text-center mb-10 lg:mb-16">
                    <h2 className="text-2xl lg:text-[2.6rem] font-bold text-white leading-snug mb-3 lg:mb-4">
                        Ideas to Elevate Your UI/UX Design
                    </h2>
                    <p className="text-sm lg:text-lg max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Innovative approaches to take your user experience to the next level.
                    </p>
                </div>

                {/* Row 1 — 1 col mobile/tablet, 3 col desktop */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-3 lg:mb-4">
                    {ideas.slice(0, 3).map((item) => (
                        <div
                            key={item.title}
                            className="idea-card relative rounded-2xl border overflow-hidden cursor-default lg:h-52"
                            style={{
                                background: "linear-gradient(135deg, rgba(147,51,234,0.06) 0%, rgba(37,99,235,0.06) 100%)",
                                borderColor: "rgba(255,255,255,0.08)",
                            }}
                        >
                            <div
                                className="hidden lg:block absolute inset-0 pointer-events-none transition-opacity duration-500 opacity-0"
                                style={{ background: "radial-gradient(circle at 50% 50%, rgba(147,51,234,0.18) 0%, transparent 70%)" }}
                            />
                            <CardContent item={item} />
                        </div>
                    ))}
                </div>

                {/* Row 2 — 1 col mobile/tablet, 2 col desktop centered */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4 lg:w-2/3 mx-auto">
                    {ideas.slice(3).map((item) => (
                        <div
                            key={item.title}
                            className="idea-card relative rounded-2xl border overflow-hidden cursor-default lg:h-52"
                            style={{
                                background: "linear-gradient(135deg, rgba(147,51,234,0.06) 0%, rgba(37,99,235,0.06) 100%)",
                                borderColor: "rgba(255,255,255,0.08)",
                            }}
                        >
                            <div
                                className="hidden lg:block absolute inset-0 pointer-events-none transition-opacity duration-500 opacity-0"
                                style={{ background: "radial-gradient(circle at 50% 50%, rgba(147,51,234,0.18) 0%, transparent 70%)" }}
                            />
                            <CardContent item={item} />
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
}